import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getRouteGpsData,
  gpsPingsWithKey,
  getGpsPingIcon,
  fetchRouteBlob,
  fetchGpsPings
} from './utils';
import { withSnackbar } from 'notistack';
import { MarkerLayer } from '../../../lib/Map';

const GpsPings = ({
  route,
  enqueueSnackbar,
  closeSnackbar,
  theme,
  showActualRoute = false,
  styleName = ''
}) => {
  const [gpsPings, setGpsPings] = useState([]);
  const [dispatchGpsPings, setDispatchGpsPings] = useState({});
  const [routesGpsPings, setRouteGpsPings] = useState({});
  const [currentDispatchPings, setCurrentDispatchPings] = useState([]);

  const getRoutesGpsPings = (route, gpsPings) => {
    const { routeId, routeStartTime, routeEndTime } = route;
    const processedData = getRouteGpsData({
      startDateTime: routeStartTime,
      endDateTime: routeEndTime,
      gpsData: gpsPings
    });
    setRouteGpsPings((prev) => ({
      ...prev,
      [routeId]: processedData
    }));
  };

  // function to process dispatch pings and update state
  const processDispatchPings = (dispatchPings, gpsData = null) => {
    const gpsDispatchPings = dispatchPings?.[route?.dispatchId];
    setDispatchGpsPings((prev) => ({
      ...prev,
      ...dispatchPings
    }));
    setCurrentDispatchPings(gpsDispatchPings);
    if (gpsData) {
      setGpsPings(gpsData);
    }
    if (route?.routeId && gpsDispatchPings?.length) {
      getRoutesGpsPings(route, gpsDispatchPings);
    }
  };

  // Memoized condition to prevent unnecessary effect triggers
  const shouldFetchGpsData = useMemo(
    () =>
      showActualRoute &&
      route?.driverId &&
      gpsPings?.length === 0 &&
      Object.keys(dispatchGpsPings).length === 0,
    [showActualRoute, route?.driverId, gpsPings?.length]
  );

  useEffect(() => {
    if (route?.routeId && currentDispatchPings?.length > 0 && !routesGpsPings[route?.routeId]) {
      getRoutesGpsPings(route, currentDispatchPings);
    }
  }, [route?.routeId, currentDispatchPings?.length]);

  useEffect(() => {
    if (shouldFetchGpsData) {
      showGPsPings();
    }
  }, [shouldFetchGpsData]);

  useEffect(() => {
    if (route?.dispatchId && gpsPings.length > 0 && showActualRoute) {
      const hasUrl = gpsPings.find((gps) => gps.dispatchId === route?.dispatchId)?.blobUrl || null;
      const dispatchPings = dispatchGpsPings[route?.dispatchId] || [];
      if (dispatchPings?.length > 0) {
        setCurrentDispatchPings(dispatchPings);
      } else if (hasUrl) {
        getGpsPings();
      } else {
        showGPsPings();
      }
    }
  }, [route?.dispatchId]);

  const getGpsPings = async () => {
    const { dispatchPings } = await fetchGpsPings({
      dispatchId: route?.dispatchId,
      gpsData: gpsPings,
      enqueueSnackbar,
      closeSnackbar,
      snackbarKey: null
    });
    processDispatchPings(dispatchPings);
  };

  const showGPsPings = useCallback(async () => {
    const { gpsData, dispatchPings } = await fetchRouteBlob({
      terminalCode: route.terminal.terminalCode,
      dispatchDate: route.dispatchDateTime,
      driverId: route.driverId,
      isLiveDispatch: route.isLiveDispatch,
      dispatchId: route.dispatchId,
      enqueueSnackbar: enqueueSnackbar,
      closeSnackbar: closeSnackbar
    });
    processDispatchPings(dispatchPings, gpsData);
  }, [route?.driverId, enqueueSnackbar, processDispatchPings]);

  const currentRouteGpsPings = useMemo(
    () => routesGpsPings[route?.routeId] || [],
    [routesGpsPings, route?.routeId]
  );

  // Memoized transformed GPS pings to prevent unnecessary recalculations
  const transformedGpsPings = useMemo(
    () => gpsPingsWithKey(currentRouteGpsPings, theme),
    [currentRouteGpsPings, theme]
  );

  // Early return if no route geometry to prevent unnecessary rendering
  if (!showActualRoute || !currentRouteGpsPings?.length) {
    return null;
  }
  
  return (
    <MarkerLayer
      layerId={`GpsPingsLayer_${route?.routeId}`}
      data={transformedGpsPings}
      dataKey={'key'}
      getIcon={getGpsPingIcon}
      showTooltip={true}
      styleName={styleName}      
    />
  );
};
export default withSnackbar(GpsPings);
